import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { Search, Eye, Receipt } from 'lucide-react';
import { displayNumber, isNumberPending } from '../lib/numbering';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

export default function Invoices() {
  const { invoices } = useData();
  const { t } = useT();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    if (!q.trim()) return invoices;
    const needle = q.toLowerCase();
    return invoices.filter(
      (inv) =>
        (inv.invoice_number || '').toLowerCase().includes(needle) ||
        (inv.client_name || '').toLowerCase().includes(needle) ||
        (inv.source_submission_number || '').toLowerCase().includes(needle)
    );
  }, [invoices, q]);

  return (
    <Layout title={t('invoice.list_title')}>
      <h1 className="font-heading text-4xl font-black tracking-tighter mb-6">{t('invoice.list_title')}</h1>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
        <input
          data-testid="invoice-search-input"
          className="field !pl-12"
          placeholder={t('hist.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="card p-8 text-center text-gray-500" data-testid="empty-invoices">
          <Receipt className="mx-auto mb-3 text-gray-300" size={36} />
          {t('invoice.empty')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv) => (
            <Link
              key={inv.id}
              to={`/invoices/${inv.id}`}
              className="card p-4 block hover:border-[#7A1F2B] transition-colors"
              data-testid={`invoice-row-${inv.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-mono text-xs ${isNumberPending(inv.invoice_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
                      {displayNumber(inv.invoice_number, t, { short: true })}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FAF7F2] text-[#7A1F2B]">
                      {t(`invoice.status.${inv.status || 'draft'}`)}
                    </span>
                  </div>
                  <div className="font-bold truncate">{inv.client_name || '—'}</div>
                  <div className="text-xs text-gray-500">
                    {inv.invoice_date || ''}
                    {inv.due_date ? ` · ${t('invoice.due_date')} ${inv.due_date}` : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-black text-lg">{money(inv.summary?.grandTotal)}</div>
                  <div className="inline-flex items-center gap-1 text-xs font-bold text-[#7A1F2B] mt-1">
                    <Eye size={12} /> {t('hist.view')}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
